import { Hamburger, NewLogo, Logout, Promote } from "@/components/bnp-icons";
import { Button } from "..";
import { BNPModal } from "@/components/bnp-ui/overlays/BNPModal";
import { CurrencySelector } from "@/components/bnp-ui/currency-selector";
import { useAuth } from "@/contexts/AuthContext";
import useNavItems from "@/hooks/useNavItems";
import { capitalizeFirstLetter } from "@/utils/capitalizeFirstLetter";
import { getInitials } from "@/utils/getInitials";
import Link from "next/link";
import { usePathname } from "next/navigation";
import React, { Fragment, useState } from "react";
import ProfessionalsDropdown from "./ProfessionalsDropdown";
const PROFESSIONAL_TITLES = ["Realtors", "Developers"];

const DashboardNav = () => {
  const { user, logout } = useAuth();
  const { navItems: menuItems } = useNavItems();
  const pathname = usePathname();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const confirmLogout = () => {
    setIsLogoutModalOpen(false);
    logout();
  };

  const professionalsItems = menuItems.filter((item) =>
    PROFESSIONAL_TITLES.includes(item.title),
  );
  const regularItems = menuItems.filter(
    (item) => !PROFESSIONAL_TITLES.includes(item.title),
  );

  return (
    <Fragment>
      <div className="w-[100%] flex flex-col lg:flex-row justify-center lg:items-center bg-bg-white border-b border-soft-200 h-[94px] lg:h-[94px]">
        <div className="font-sans flex justify-between items-center bg-bg-white w-full max-w-[1440px] pt-3 lg:pt-8 px-4 pb-3 lg:px-[44px] lg:py-8 h-[64px] lg:h-[94px] relative">
          <div className="justify-start items-center flex gap-1 pl-3 lg:pl-0">
<Link
  href="/"
  className="justify-start items-center flex gap-2 hover:opacity-90 transition-opacity"
  aria-label="FindNigeriaProperties.com Home"
  style={{ minHeight: "44px" }}
>
  <img
    src="/fnp-logo-with-credibility-subtext-dark-teal.png"
    alt="FindNigeriaProperties.com"
    className="w-auto h-auto max-w-[220px] relative top-1 object-contain"
  />
</Link>
          </div>

          <div className="hidden lg:flex items-center justify-center gap-6 text-blue-primary font-semibold text-base">
            {regularItems.map((item, index: number) => (
              <Fragment key={index}>
                <Link href={item?.path} className="relative py-2 text-sm font-semibold transition-colors hover:text-bnp-cyan whitespace-nowrap">
                  {item?.title === "Deals" ? (
                    <span className="inline-flex items-center gap-1">
                      <span className="inline-flex items-center justify-center w-5 h-5 bg-red-base rounded-full">
                        <Promote />
                      </span>
                      {item?.title}
                    </span>
                  ) : (
                    item?.title
                  )}
                </Link>
                {item?.title === "Property Request" &&
                  professionalsItems.length > 0 && (
                    <ProfessionalsDropdown
                      items={professionalsItems}
                      pathname={pathname}
                    />
                  )}
              </Fragment>
            ))}
          </div>

          <div className="hidden lg:flex justify-center items-center gap-[14px]">
            <CurrencySelector />
            <div className="flex gap-2 items-center p-4 border-[1.5px] border-grey-2 shadow-sm rounded-[8px] w-[155px] h-[48px]">
              {user?.profile_picture ? (
                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden cursor-pointer">
                  <img
                    src={user?.profile_picture}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="flex justify-center items-center p-2 w-8 h-8 text-white bg-bnp-cyan rounded-full font-bold text-xs">
                  {getInitials(user?.full_name ?? "".toUpperCase())}
                </div>
              )}
              <p
                className="font-medium text-sm text-blue-primary w-fit max-w-[50%] truncate"
                title={capitalizeFirstLetter(user?.full_name ?? "")}
              >
                {capitalizeFirstLetter(user?.full_name ?? "")}
              </p>
              <div
                className="ml-auto cursor-pointer"
                onClick={() => setIsLogoutModalOpen(true)}
                title="Logout"
              >
                <Logout />
              </div>
            </div>
          </div>

          <div className="flex gap-2 lg:hidden pr-3">
            <Hamburger onClick={toggleMenu} />
          </div>
        </div>

        {isMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-50">
            <div
              className="fixed inset-0 bg-black bg-opacity-50"
              onClick={toggleMenu}
            />

            <div className="font-sans fixed right-0 top-0 h-full w-[80%] max-w-[300px] bg-white shadow-lg transform transition-transform duration-300 ease-in-out">
              <div className="flex flex-col p-6">
                <div className="flex justify-end mb-6">
                  <button
                    onClick={toggleMenu}
                    className="text-bnp-dark"
                    aria-label="Close menu"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                <div className="pb-1">
                  <CurrencySelector />
                </div>

                <nav className="flex flex-col gap-4">
                  {menuItems.map((item, index: number) => (
                    <Link
                      href={item?.path}
                      key={index}
                      className="text-blue-primary font-semibold text-sm py-2 hover:bg-gray-100 rounded px-2"
                      onClick={toggleMenu}
                    >
                      {item?.title === "Deals" ? (
                        <span className="inline-flex items-center gap-1">
                          <span className="inline-flex items-center justify-center w-5 h-5 bg-red-base rounded-full">
                            <Promote />
                          </span>
                          {item?.title}
                        </span>
                      ) : (
                        item?.title
                      )}
                    </Link>
                  ))}
                </nav>

                <div className="flex flex-col gap-4 mt-6">
                  <div className="flex gap-2 items-center p-4 border-[1.5px] border-grey-2 shadow-sm rounded-[8px] w-[155px] h-[48px]">
                    {user?.profile_picture ? (
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden cursor-pointer">
                        <img
                          src={user?.profile_picture}
                          alt="Profile"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="flex justify-center items-center p-2 w-8 h-8 text-white bg-bnp-cyan rounded-full font-bold text-xs">
                        {getInitials(user?.full_name ?? "".toUpperCase())}
                      </div>
                    )}

                    <p
                      className="font-medium text-sm text-blue-primary w-fit max-w-[50%] truncate"
                      title={capitalizeFirstLetter(user?.full_name ?? "")}
                    >
                      {capitalizeFirstLetter(user?.full_name ?? "")}
                    </p>
                    <div
                      className="ml-auto cursor-pointer"
                      onClick={() => setIsLogoutModalOpen(true)}
                      title="Logout"
                    >
                      <Logout />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <BNPModal
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        maxWidth="max-w-md"
      >
        <div className="flex flex-col items-center text-center p-8">
          <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <Logout />
          </div>
          <h3 className="text-lg font-semibold text-bnp-dark">
            Confirm Logout
          </h3>
          <p className="mt-2 text-sm text-gray-600">
            Are you sure you want to log out of your account?
          </p>
          <div className="flex gap-3 mt-6 w-full">
            <Button
              variant="secondary"
              isFullWidth
              onClick={() => setIsLogoutModalOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="danger" isFullWidth onClick={confirmLogout}>
              Logout
            </Button>
          </div>
        </div>
      </BNPModal>
    </Fragment>
  );
};

export default DashboardNav;
